import axios from "axios";
import { format as formatDate } from "date-fns";
import { dateFnsOptions } from "../_utils/dateFnsLocale";
import { handleHTTPError } from "./errorHandlerActions"
import {
  ANALYTICS_ENABLE,
  ANALYTICS_RANGE_DATA,
  ANALYTICS_RANGE_POSTS_DATA,
  ANALYTICS_STATUS,
  ANALYTICS_REPORT_ENDPOINT,
  ANALYTICS_REPORT_PREVIEW,
} from "./endpoints";
import {
  SET_ANALYTICS_LOADING,
  SET_ANALYTICS_STATUS,
  SET_ANALYTICS_CHARTS_DATA,
  SET_ANALYTICS_STATIC_DATA,
  SET_ANALYTICS_DATE_RANGE,
  SET_ANALYTICS_SELECTED_METRICS,
  SET_ANALYTICS_POSTS,
  SET_ANALYTICS_POSTS_METRICS,
  SET_ANALYTICS_POSTS_PAGINATION,
  SET_ANALYTICS_POSTS_ORDER,
  SET_ANALYTICS_CHART_METRICS,
  CLONE_ANALYTICS_CHART,
  REMOVE_ANALYTICS_CHART,
} from "./types";


const mapRangeResponseToChartData = (response) => {
  const chartData = [];

  response.data[0].metrics.forEach((metric) => {
    chartData.push({
      id: metric.id,
      label: metric.label,
      data: [],
    });
  });

  response.data
    .sort((a, b) => a.date.localeCompare(b.date)) // backend returns non sorted results (random dates order), so sorting is required to be drawn correctly on chart
    .forEach((d) => {
      d.metrics.forEach((metric, metricIndex) => {
        chartData[metricIndex].data.push({
          x: d.date,
          y: metric.value,
          yPrev: metric.prevValue
        })
      });
    });

  return chartData;
}

export const setAnalyticsLoading = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_LOADING,
    payload,
  });
};

export const setAnalyticsStatus = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_STATUS,
    payload,
  });
};

export const setAnalyticsDateRange = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_DATE_RANGE,
    payload,
  });
};

export const setAnalyticsPostsPagination = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_POSTS_PAGINATION,
    payload,
  });
};

export const setAnalyticsPostsOrder = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_POSTS_ORDER,
    payload,
  });
};

export const setAnalyticsPostsMetrics = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_POSTS_METRICS,
    payload,
  });
};

export const setAnalyticsRangeChartsData = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_CHARTS_DATA,
    payload
  });
};

export const setAnalyticsStaticMetricsData = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_STATIC_DATA,
    payload
  });
};

export const setAnalyticsSelectedMetrics = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_SELECTED_METRICS,
    payload
  });
};

export const setChartMetrics = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_CHART_METRICS,
    payload
  });
};

export const setAnalyticsPosts = payload => dispatch => {
  dispatch({
    type: SET_ANALYTICS_POSTS,
    payload
  });
};

export const cloneChart = payload => dispatch => {
  dispatch({
    type: CLONE_ANALYTICS_CHART,
    payload
  });
};

export const removeChart = payload => dispatch => {
  dispatch({
    type: REMOVE_ANALYTICS_CHART,
    payload
  });
};

export const getAnalyticsStatus =
  (accountId) =>
    (dispatch) => {
      dispatch(setAnalyticsLoading(true));

      return new Promise((resolve, reject) => {
        axios
          .get(ANALYTICS_STATUS, {
            params: { accountIds: accountId },
          })
          .then((res) => {
            dispatch(setAnalyticsStatus(res.data.statuses[accountId]));
            resolve(res);
            dispatch(setAnalyticsLoading(false));
          })
          .catch((error) => {
            reject(error);
            dispatch(setAnalyticsLoading(false));
          });
      });
    };

export const getAnalyticsStatuses =
  (accountIds) =>
    (dispatch) => {
      return new Promise((resolve, reject) => {
        axios
          .get(ANALYTICS_STATUS, {
            params: { accountIds: accountIds.join(',')},
          })
          .then((res) => {
            resolve(res);
          })
          .catch((error) => {
            dispatch(handleHTTPError(error));
          });
      });
    };

export const enableAnalytics =
  (accountId) =>
    (dispatch) => {
      dispatch(setAnalyticsLoading(true));

      return new Promise((resolve, reject) => {
        axios
          .post(ANALYTICS_ENABLE, { accountId })
          .then((res) => {
            resolve(res);
            dispatch(setAnalyticsLoading(false));
          })
          .catch((error) => {
            reject(error);
            dispatch(setAnalyticsLoading(false));
          });
      });
    };

export const getAnalyticsData =
  ({ accountId, timezone, from, to, metrics }) =>
    (dispatch) => {
      dispatch(setAnalyticsLoading(true));

      return new Promise((resolve, reject) => {
        axios
          .post(ANALYTICS_RANGE_DATA, {
            accountId,
            metrics,
            from,
            to,
            tz: timezone
          })
          .then((res) => {
            dispatch(setAnalyticsRangeChartsData(mapRangeResponseToChartData(res.data)));
            // some metrics are not available with date based splits and sent separately, so need separate handling
            dispatch(setAnalyticsStaticMetricsData(res.data.staticMetrics || []));
            resolve(res);
            dispatch(setAnalyticsLoading(false));
          })
          .catch((error) => {
            reject(error);
            dispatch(setAnalyticsLoading(false));
          });
      });
    };

export const getAnalyticsPosts =
  ({ accountId, timezone, from, to, page, perPage, order, orderBy }) =>
    (dispatch) => {
      const params = {
        accountId,
        page,
        perPage,
        from,
        to,
        tz: timezone,
        order,
        orderBy,
      };

      dispatch(setAnalyticsLoading(true));

      return new Promise((resolve, reject) => {
        axios
          .get(ANALYTICS_RANGE_POSTS_DATA, { params })
          .then((res) => {
            dispatch(setAnalyticsPosts(res.data.posts));
            dispatch(setAnalyticsPostsPagination({
              pagesCount: res.data.pages_count,
            }));
            resolve(res);
            dispatch(setAnalyticsLoading(false));
          })
          .catch((error) => {
            reject(error);
            dispatch(setAnalyticsLoading(false));
          });
      });
    };

const getPosts = (params) => axios.get(ANALYTICS_RANGE_POSTS_DATA, { params });
const getRangeData = (params) => axios.post(ANALYTICS_RANGE_DATA, params);

export const downloadAnalyticsReport =
  ({
    account,
    availableMetrics,
    fileType,
    metrics,
    metricsIds,
    timezone,
    from,
    to,
    order,
    orderBy,
    visibleCharts,
  }) =>
    async (dispatch) => {
      try {
        dispatch(setAnalyticsLoading(true));

        const [responsePosts, responseRangeData] = await Promise.all([
          getPosts({
            account,
            accountId: account.id,
            page: 1,
            perPage: 999999999,
            from,
            to,
            tz: timezone,
            order,
            orderBy,
          }),
          getRangeData({
            accountId: account.id,
            from,
            to,
            tz: timezone,
            metrics: metricsIds,
          }),
        ]);

        const createdDate = formatDate(new Date(), "p, dd.MM.yyyy", dateFnsOptions());
        const analyticsReportData = {
          account,
          availableMetrics,
          // specific chart width to work correctly on aws puppeteer rendering;
          chartWidth: fileType === "pdf" ? "1020px" : "1240px",
          posts: {
            items: responsePosts.data.posts,
          },
          createdDate,
          chartsData: mapRangeResponseToChartData(responseRangeData.data),
          staticMetrics: responseRangeData.data.staticMetrics,
          from,
          to,
          metrics,
          visibleCharts,
        };

        const data = {
          url: ANALYTICS_REPORT_PREVIEW,
          pageData: analyticsReportData,
          fileType,
        }

        if (fileType === "pdf") {
          data.pdf = {
            format: "a4",
            scale: 0.75,
          }
        } else {
          data.pageSize = {
            width: 1280,
            height: 1080,
          }
          data.fullPage = true;
        }

        const renderResponse = await axios({
          method: "post",
          url: ANALYTICS_REPORT_ENDPOINT,
          data,
        });

        dispatch(setAnalyticsLoading(false));

        return renderResponse;
      } catch (error) {
        dispatch(setAnalyticsLoading(false));
        dispatch(handleHTTPError(error));
      }
    };

